'use client'

import type { ButtonProps, TextProps } from '@chakra-ui/react'
import {
  Button,
  createContext,
  IconButton,
  Pagination as ChakraPagination,
  Text,
  usePaginationContext,
} from '@chakra-ui/react'
import * as React from 'react'
import { HiMiniEllipsisHorizontal } from 'react-icons/hi2'

import { ArrowLeftIcon, ArrowRightIcon } from '../icon'
import { LinkButton } from './link-button'

interface ButtonVariantMap {
  current: ButtonProps['variant']
  default: ButtonProps['variant']
  ellipsis: ButtonProps['variant']
}

type PaginationVariant = 'outline' | 'solid' | 'subtle'

interface ButtonVariantContext {
  size: ButtonProps['size']
  variantMap: ButtonVariantMap
  getHref?: (page: number) => string
}

const [RootPropsProvider, useRootProps] = createContext<ButtonVariantContext>({
  name: 'RootPropsProvider',
})

export interface PaginationRootProps
  extends Omit<ChakraPagination.RootProps, 'type'> {
  size?: ButtonProps['size']
  variant?: PaginationVariant
  getHref?: (page: number) => string
}

const variantMap: Record<PaginationVariant, ButtonVariantMap> = {
  outline: { default: 'ghost', ellipsis: 'plain', current: 'outline' },
  solid: { default: 'outline', ellipsis: 'outline', current: 'solid' },
  subtle: { default: 'ghost', ellipsis: 'plain', current: 'subtle' },
}

export const PaginationRoot = React.forwardRef<
  HTMLDivElement,
  PaginationRootProps
>(function PaginationRoot(props, ref) {
  const { size = 'sm', variant = 'outline', getHref, ...rest } = props
  return (
    <RootPropsProvider
      value={{ size, variantMap: variantMap[variant], getHref }}
    >
      <ChakraPagination.Root
        ref={ref}
        type={getHref ? 'link' : 'button'}
        siblingCount={4}
        {...rest}
      />
    </RootPropsProvider>
  )
})

export const PaginationEllipsis = React.forwardRef<
  HTMLDivElement,
  ChakraPagination.EllipsisProps
>(function PaginationEllipsis(props, ref) {
  const { size, variantMap } = useRootProps()
  return (
    <ChakraPagination.Ellipsis ref={ref} {...props} asChild>
      <Button
        as="span"
        variant={variantMap.ellipsis}
        size={size}
        color="colorPalette.500"
      >
        <HiMiniEllipsisHorizontal />
      </Button>
    </ChakraPagination.Ellipsis>
  )
})

export const PaginationItem = React.forwardRef<
  HTMLButtonElement,
  ChakraPagination.ItemProps
>(function PaginationItem(props, ref) {
  const { page } = usePaginationContext()
  const { variantMap, getHref } = useRootProps()

  const current = page === props.value
  const variant = current ? variantMap.current : variantMap.default

  if (getHref) {
    return (
      <LinkButton
        href={getHref(props.value)}
        variant={variant}
        color="colorPalette.500"
      >
        {props.value}
      </LinkButton>
    )
  }

  return (
    <ChakraPagination.Item
      minW="32px"
      h="32px"
      boxSizing="border-box"
      p={0}
      ref={ref}
      {...props}
      asChild
    >
      <Button
        variant={variant}
        color="colorPalette.500"
        borderRadius="base"
        _selected={{
          border: 'none',
          bgColor: 'blue.100',
        }}
      >
        {props.value}
      </Button>
    </ChakraPagination.Item>
  )
})

export const PaginationPrevTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraPagination.PrevTriggerProps
>(function PaginationPrevTrigger(props, ref) {
  const { size, variantMap, getHref } = useRootProps()
  const { previousPage } = usePaginationContext()

  if (getHref) {
    return (
      <LinkButton
        href={previousPage != null ? getHref(previousPage) : undefined}
        variant={variantMap.default}
        size={size}
        color="colorPalette.500"
      >
        <ArrowLeftIcon />
      </LinkButton>
    )
  }

  return (
    <ChakraPagination.PrevTrigger
      minW="32px"
      h="32px"
      boxSizing="border-box"
      p={0}
      ref={ref}
      asChild
      {...props}
    >
      <IconButton
        variant={variantMap.default}
        size={size}
        color="colorPalette.500"
        borderRadius="base"
        _selected={{
          border: 'none',
          bgColor: 'blue.100',
        }}
      >
        <ArrowLeftIcon />
      </IconButton>
    </ChakraPagination.PrevTrigger>
  )
})

export const PaginationNextTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraPagination.NextTriggerProps
>(function PaginationNextTrigger(props, ref) {
  const { size, variantMap, getHref } = useRootProps()
  const { nextPage, count } = usePaginationContext()
  const disabled = count === 0

  if (getHref) {
    return (
      <LinkButton
        href={nextPage != null ? getHref(nextPage) : undefined}
        variant={variantMap.default}
        size={size}
        color="colorPalette.500"
      >
        <ArrowRightIcon />
      </LinkButton>
    )
  }

  return (
    <ChakraPagination.NextTrigger
      minW="32px"
      h="32px"
      boxSizing="border-box"
      p={0}
      ref={ref}
      asChild
      {...props}
    >
      <IconButton
        disabled={disabled}
        variant={variantMap.default}
        size={size}
        color="colorPalette.500"
        borderRadius="base"
        _selected={{
          border: 'none',
          bgColor: 'blue.100',
        }}
      >
        <ArrowRightIcon />
      </IconButton>
    </ChakraPagination.NextTrigger>
  )
})

export const PaginationItems = (props: React.HTMLAttributes<HTMLElement>) => {
  const { page: currentPage } = usePaginationContext()
  return (
    <ChakraPagination.Context>
      {({ pages: items }) => {
        // NOTE:
        // 表示するボタンの数を5個に固定する
        // RootコンポーネントのsiblingCount={4}と併用して実現する
        const pages = items.filter(item => item.type === 'page')
        const lastPage = pages[pages.length - 1]?.value
        const currentIndex = pages.findIndex(page => page.value === currentPage)

        let trimmedPages =
          pages.length !== 0 ? pages : [{ type: 'page', value: 1 }]
        if (lastPage > 5) {
          if (currentPage <= 2) {
            trimmedPages = pages.slice(0, 5)
          } else if (currentPage >= lastPage - 1) {
            trimmedPages = pages.slice(-5)
          } else {
            trimmedPages = pages.slice(currentIndex - 2, currentIndex + 3)
          }
        }

        return trimmedPages.map((page, index) => (
          <PaginationItem
            key={index}
            type="page"
            value={page.value}
            {...props}
          />
        ))
      }}
    </ChakraPagination.Context>
  )
}

interface PageTextProps extends TextProps {
  format?: 'short' | 'compact' | 'long'
}

export const PaginationPageText = React.forwardRef<
  HTMLParagraphElement,
  PageTextProps
>(function PaginationPageText(props, ref) {
  const { format = 'long', ...rest } = props
  const { page, totalPages, pageRange, count } = usePaginationContext()
  const content = React.useMemo(() => {
    if (format === 'short') return `${page} / ${totalPages}`
    if (format === 'compact') return `${page} of ${totalPages}`

    return count === 0
      ? `${Math.min(pageRange.end, count)}件表示 / ${count}件`
      : `${pageRange.start + 1}~${Math.min(pageRange.end, count)}件表示 / ${count}件`
  }, [format, page, totalPages, pageRange, count])

  return (
    <Text ref={ref} fontSize="sm" color="gray.700" {...rest}>
      {content}
    </Text>
  )
})
